import React, {
  Dispatch,
  FC,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useHistory, useLocation } from 'react-router'
import styled, { keyframes } from 'styled-components'
import { View } from 'src/modules/components/lib/view'
import { useViewportHeight } from 'src/modules/hooks/useViewportHeight'
import {
  AFTER_7PM_VALUE,
  PHARMACY_SERVICE_LIST,
} from '../../constants/search-condition'

import { Clickable } from '../lib/clickable'
import { Close2 } from '../lib/curon-icon'
import { Heading } from '../lib/heading'
import { ModalContext, useModalStackDepth } from '../lib/modal'
import { MedicinePickupSelectDayFilter } from './medicine-pickup-select-day-filter'

const ToVisibleKeyFrames = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`

const ToInvisibleKeyFrames = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
`
type ConditionContainerRootProps = {
  containerRootHeight: string | number
}

const ConditionContainerRoot = styled.div<ConditionContainerRootProps>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.containerRootHeight};
  background: white;
  visibility: hidden;
  &.visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    animation: ${ToVisibleKeyFrames} 200ms ease-out 0s;
  }
  &.invisible {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    animation: ${ToInvisibleKeyFrames} 200ms ease-out 0s;
    animation-fill-mode: forwards;
  }
`

const ConditionElementRoot = styled.div<{ selected: boolean }>`
  display: flex;
  height: 40px;
  padding: 0px 12px;
  align-items: center;
  gap: 8px;
  border-radius: 24px;
  border-width: 1px;
  border-style: solid;
  border-color: ${props => (props.selected ? '#6C9' : '#A5B1C0')};
  background: ${props => (props.selected ? '#ECF9F2' : 'white')};
  font-size: 12px;
`

const CategoryListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 12px;
`

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding-top: 16px;

  &:not(:first-child) {
    border-top: 1px solid #e8e8e8;
    margin-top: 16px;
  }

  &:not(:first-child::before) {
    content: '';
    display: block;
    height: 1px;
    background-color: #e8e8e8;
    width: calc(100% - 32px);
    margin: 0 auto;
  }
`

const CategoryHeader = styled.div`
  width: 100%;
  font-weight: 600;
  font-size: 12px;
  color: #667587;
`

const OverlayControl = styled(View)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  z-index: 2;
  padding: 16px;
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.08),
    0px 1px 12px 0px rgba(0, 0, 0, 0.16);
`

const FilterButton = styled.a<{ disabled: boolean }>`
  display: flex;
  height: 40px;
  width: 186px;
  padding: 0px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: ${props => (props.disabled ? '#D3DDE8' : '#2BA168')};
  color: ${props => (props.disabled ? '#637182' : '#fff')};
  color: var(--CDS-Static-White, #fff);
  text-align: center;
  font-family: 'Hiragino Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`

const ClearLink = styled.div`
  display: flex;
  width: 109px;
  height: 32px;
  padding: 0px 12px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

const ConditionContainer: FC<
  React.HTMLAttributes<HTMLDivElement> & {
    visible: boolean
  }
> = ({ visible, ...props }) => {
  const context = useContext(ModalContext)
  const stackDepth = useModalStackDepth(visible)
  const vh = useViewportHeight()

  if (!context.root) {
    return null
  }

  return ReactDOM.createPortal(
    <ConditionContainerRoot
      style={{ zIndex: stackDepth }}
      className={visible ? 'visible' : 'invisible'}
      containerRootHeight={`calc(${vh * 100}px)`}
      {...props}
    >
      {props.children}
    </ConditionContainerRoot>,
    context.root,
  )
}

type ServiceListProps = {
  category: string
  selectedList: Array<string>
  updateSelectedList: Dispatch<{ type: 'append' | 'delete'; value: string }>
}

const ServiceList: FC<ServiceListProps> = props => (
  <CategoryListContainer>
    {PHARMACY_SERVICE_LIST.filter(
      service => service.category === props.category,
    ).map(service => {
      const selected = props.selectedList.includes(service.value)
      return (
        <ConditionElementRoot key={service.value} selected={selected}>
          <Clickable
            onClick={() =>
              props.updateSelectedList({
                type: selected ? 'delete' : 'append',
                value: service.value,
              })
            }
          >
            {service.label}
          </Clickable>
        </ConditionElementRoot>
      )
    })}
  </CategoryListContainer>
)

type Props = {
  visible: boolean
  defaultConditions: Record<string, string>
  onCloseClick: () => void
  /* onSearch: (selectedList: Array<string>) => void */
}

export const ConditionModal: FC<Props> = props => {
  const location = useLocation()
  const history = useHistory()

  const { visible, defaultConditions, onCloseClick } = props
  const [selectedValue, setSelectedValue] = useState('0')
  const [searchConditions] = useState<string[]>(Object.keys(defaultConditions))

  const getSelectedDay = () => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.get('selected_day') ?? '0'
  }

  useEffect(() => {
    const selectedDay = getSelectedDay()
    if (selectedDay) {
      setSelectedValue(selectedDay)
    }
  }, [location.search])

  const [selectedList, updateSelectedList] = useReducer(
    (
      state: Array<string>,
      action: { type: 'append' | 'delete' | 'clear'; value?: string },
    ) => {
      switch (action.type) {
        case 'append':
          if (action.value) {
            return state.concat([action.value])
          }
          return state
        case 'delete':
          if (action.value) {
            return state.filter(v => v !== action.value)
          }
          return state
        case 'clear':
          return []
        default:
          return state
      }
    },
    searchConditions,
  )

  const buttonDisabled =
    selectedList === searchConditions && selectedValue === getSelectedDay()

  const handleFilterButtonClick = () => {
    onCloseClick()

    const currentParams = new URLSearchParams(location.search)
    const updatedParams = updateSearchParams(currentParams)
    history.push({
      search: updatedParams.toString(),
    })
  }

  const updateSearchParams = (currentParams: URLSearchParams) => {
    for (const key of Array.from(currentParams.keys())) {
      if (
        !selectedList.includes(key) &&
        (PHARMACY_SERVICE_LIST.some(service => service.value === key) ||
          key === AFTER_7PM_VALUE)
      ) {
        currentParams.delete(key)
      }
    }

    selectedList.forEach((value: string) => {
      if (
        PHARMACY_SERVICE_LIST.some(service => service.value === value) ||
        value === AFTER_7PM_VALUE
      ) {
        currentParams.set(value, '1')
      }
    })
    currentParams.set('selected_day', selectedValue)

    return currentParams
  }

  return (
    <ConditionContainer visible={visible}>
      <View position="relative" m={4}>
        <Heading fontSize="16px" textAlign="left">
          絞り込み
        </Heading>
        <Clickable
          onClick={() => {
            updateSelectedList({ type: 'clear' })
            searchConditions.forEach(condition => {
              updateSelectedList({ type: 'append', value: condition })
            })
            onCloseClick()
          }}
          position="absolute"
          right="16px"
          top="4px"
        >
          <Close2 size="16px" color="shuttleGray" />
        </Clickable>
      </View>

      <View
        overflowY={'scroll'}
        flexGrow={1}
        style={{ overscrollBehavior: 'none' }}
      >
        <View width="100%" p={4}>
          <CategoryContainer>
            <CategoryHeader>お薬の受取希望日</CategoryHeader>
            <MedicinePickupSelectDayFilter
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
            />
          </CategoryContainer>
          <CategoryContainer>
            <CategoryHeader>営業時間</CategoryHeader>
            <CategoryListContainer>
              <ConditionElementRoot
                key={AFTER_7PM_VALUE}
                selected={selectedList.includes(AFTER_7PM_VALUE)}
              >
                <Clickable
                  onClick={() =>
                    updateSelectedList({
                      type: selectedList.includes(AFTER_7PM_VALUE)
                        ? 'delete'
                        : 'append',
                      value: AFTER_7PM_VALUE,
                    })
                  }
                >
                  19時以降
                </Clickable>
              </ConditionElementRoot>
            </CategoryListContainer>
          </CategoryContainer>

          <CategoryContainer>
            <CategoryHeader>店頭での決済方法</CategoryHeader>
            <ServiceList
              selectedList={selectedList}
              updateSelectedList={updateSelectedList}
              category="payment"
            />
          </CategoryContainer>
          <CategoryContainer>
            <CategoryHeader>施設情報</CategoryHeader>
            <ServiceList
              selectedList={selectedList}
              updateSelectedList={updateSelectedList}
              category="facility"
            />
          </CategoryContainer>
          <CategoryContainer>
            <CategoryHeader>お薬情報</CategoryHeader>
            <ServiceList
              selectedList={selectedList}
              updateSelectedList={updateSelectedList}
              category="medicine_information"
            />
          </CategoryContainer>
          <CategoryContainer>
            <CategoryHeader>その他</CategoryHeader>
            <ServiceList
              selectedList={selectedList}
              updateSelectedList={updateSelectedList}
              category="other"
            />
          </CategoryContainer>
        </View>
      </View>

      <OverlayControl>
        <ClearLink
          onClick={() => {
            setSelectedValue(getSelectedDay())
            updateSelectedList({ type: 'clear' })
          }}
        >
          クリア
        </ClearLink>
        <FilterButton
          disabled={buttonDisabled}
          onClick={handleFilterButtonClick}
        >
          検索結果を見る
        </FilterButton>
      </OverlayControl>
    </ConditionContainer>
  )
}
